import { useEffect, useState } from 'react';
import { Organization, Role } from '../../types';

type FindRoleFunction = (
  roleId: string,
  organizationId: string,
  options?: { bypassCache?: boolean }
) => Promise<Role>;

type UseRoleMetadataOptions = {
  currentOrganization?: Organization;
  findRole?: FindRoleFunction;
  roles: Role[];
  organizationId?: string;
};

export function useRoleMetadata(options: UseRoleMetadataOptions): boolean {
  const { currentOrganization, findRole, roles, organizationId } = options;
  const [rolesMetadataFetched, setRolesMetadataFetched] = useState(false);
  const allowUnavailableRoles = currentOrganization?.allowUnavailableRoles;

  const rolesKey = roles
    .map((role) => `${role.id}:${role.organizationId || organizationId}`)
    .join(',');

  useEffect(() => {
    if (allowUnavailableRoles === 'all') {
      setRolesMetadataFetched(true);
      return;
    }

    const shouldFetchMetadata =
      allowUnavailableRoles === 'individual' &&
      !!findRole &&
      roles.length > 0 &&
      roles.some((role) => role.organizationId || organizationId);

    if (!shouldFetchMetadata) {
      setRolesMetadataFetched(true);
      return;
    }

    let isCancelled = false;

    async function fetchRolesMetadata() {
      try {
        await Promise.all(
          roles.map(async (role) => {
            if (!isCancelled) {
              try {
                const orgId = role.organizationId || organizationId;
                if (orgId) {
                  await findRole?.(role.id, orgId, { bypassCache: true });
                }
              } catch (error) {
                console.error(`Failed to fetch metadata for role ${role.id}:`, error);
              }
            }
          })
        );
        if (!isCancelled) {
          setRolesMetadataFetched(true);
        }
      } catch (error) {
        console.error('Error fetching roles metadata:', error);
        if (!isCancelled) {
          setRolesMetadataFetched(true);
        }
      }
    }

    setRolesMetadataFetched(false);
    fetchRolesMetadata();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolesKey, findRole, allowUnavailableRoles, organizationId]);

  return rolesMetadataFetched;
}
